import React, { useCallback, useRef, useState } from "react"

import { Input } from "@coval/ui"

interface TagNameEditorProps {
  tag: string
  onRename: (nextTag: string) => void
  onEditingChange?: (isEditing: boolean) => void
  disabled?: boolean
  isPending?: boolean
}

/**
 * A tag name that becomes a field where it sits.
 *
 * Renaming belongs to the organization rather than to the record being tagged,
 * so the mutation is wired in the app layer and the menu only lends the slot.
 * The editor reports when it opens: the menu holding it treats a rename in
 * progress as a reason to stay open.
 */
export const TagNameEditor: React.FC<TagNameEditorProps> = ({
  tag,
  onRename,
  onEditingChange,
  disabled,
  isPending,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(tag)
  // Enter and Escape both unmount the field, and a browser that fires blur on
  // the way out would otherwise save a second time — or save what Escape just
  // discarded.
  const isFinishedRef = useRef(false)
  const isUpdateDisabled = disabled || isPending

  const setEditing = useCallback(
    (nextEditing: boolean) => {
      setIsEditing(nextEditing)
      onEditingChange?.(nextEditing)
    },
    [onEditingChange],
  )

  const startEditing = useCallback(() => {
    if (isUpdateDisabled) {
      return
    }
    isFinishedRef.current = false
    setDraft(tag)
    setEditing(true)
  }, [isUpdateDisabled, setEditing, tag])

  const finishEditing = useCallback(
    (shouldSave: boolean) => {
      if (isFinishedRef.current) {
        return
      }
      isFinishedRef.current = true
      const nextTag = draft.trim()
      if (shouldSave && nextTag && nextTag !== tag) {
        onRename(nextTag)
      }
      setEditing(false)
    },
    [draft, onRename, setEditing, tag],
  )

  if (isEditing) {
    return (
      <Input
        autoFocus
        aria-label={`Rename ${tag}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => finishEditing(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            finishEditing(true)
          }
          if (event.key === "Escape") {
            event.preventDefault()
            finishEditing(false)
          }
        }}
        // Grows harder than the add target beside it in an unselected row, so
        // the field claims most of the width it shares.
        className="h-5 min-w-0 shrink basis-0 grow-[3] px-1 py-0 text-sm"
      />
    )
  }

  if (isUpdateDisabled) {
    return (
      <span className="min-w-0 shrink truncate px-1" title={tag}>
        {tag}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      aria-label={`Rename ${tag}`}
      title={`Rename ${tag}`}
      className="min-w-0 shrink cursor-text truncate rounded-sm px-1 text-left hover:bg-surface-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border"
    >
      {tag}
    </button>
  )
}
